/**
 * The data behind the CS "Issues by tag" report: ant's issueMetrics query,
 * the systems and partners its filters offer, and the CSV of the same
 * numbers. The GraphQL contract is pinned in
 * src/app/hooks/chat/issueMetricsContract.graphql until the schema copy
 * carries it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getCredentials } from '../../../api/http';
import { postGraphQL } from '../../../hooks/chat/client';
import { runtimeConfig } from '../../../util/runtimeConfig';
import { downloadBlob } from '../../../utils';

// ----------------------------------------------------------------------------
// Shapes, as ant returns them
// ----------------------------------------------------------------------------

export type IssueSource = 'CS_CHAT' | 'RIDER_APP';

export interface IssueTagCount {
  tagId: string;
  label: string;
  /** Null for a tag outside the glossary. */
  categoryId: string | null;
  /** Tagged by an agent on a chat. */
  csChat: number;
  /** Picked by a rider in the app's report flow. */
  riderApp: number;
  /** Added by the app on its own (failed_to_unlock after a failed unlock). */
  riderAppAuto: number;
  total: number;
}

export interface IssueCategoryCount {
  categoryId: string;
  label: string;
  csChat: number;
  riderApp: number;
  riderAppAuto: number;
  total: number;
}

export interface IssueAgentCount {
  adminId: string;
  name: string;
  sessionsHandled: number;
  sessionsClosed: number;
  sessionsTagged: number;
  medianFirstResponseSeconds: number | null;
  csat: number | null;
}

export interface IssueSystemCount {
  systemId: string;
  name: string;
  partnerName: string | null;
  csChat: number;
  riderApp: number;
  total: number;
}

export interface IssueMetrics {
  from: string;
  to: string;
  sessions: number;
  taggedSessions: number;
  untaggedSessions: number;
  assistantOnlySessions: number;
  riderReports: number;
  byTag: IssueTagCount[];
  byCategory: IssueCategoryCount[];
  byAgent: IssueAgentCount[];
  bySystem: IssueSystemCount[];
}

/** The query's arguments, as the filter bar builds them. */
export interface IssueMetricsVariables {
  from: string;
  to: string;
  /** The partner's legacy integer id. */
  partnerId: number | null;
  systemId: string | null;
  /** ant's product family atom: "bike", "kayak", ... */
  hardwareFamily: string | null;
  /** Null for every source. */
  sources: IssueSource[] | null;
  countsAsNew: boolean;
}

// ----------------------------------------------------------------------------
// issueMetrics
// ----------------------------------------------------------------------------

export const ISSUE_METRICS_QUERY = `
  query SupportIssueMetrics(
    $from: DateTime!
    $to: DateTime!
    $partnerId: Int
    $systemId: ID
    $hardwareFamily: String
    $sources: [IssueSource!]
    $countsAsNew: Boolean
  ) {
    issueMetrics(
      from: $from
      to: $to
      partnerId: $partnerId
      systemId: $systemId
      hardwareFamily: $hardwareFamily
      sources: $sources
      countsAsNew: $countsAsNew
    ) {
      from
      to
      sessions
      taggedSessions
      untaggedSessions
      assistantOnlySessions
      riderReports
      byTag {
        tagId
        label
        categoryId
        csChat
        riderApp
        riderAppAuto
        total
      }
      byCategory {
        categoryId
        label
        csChat
        riderApp
        riderAppAuto
        total
      }
      byAgent {
        adminId
        name
        sessionsHandled
        sessionsClosed
        sessionsTagged
        medianFirstResponseSeconds
        csat
      }
      bySystem {
        systemId
        name
        partnerName
        csChat
        riderApp
        total
      }
    }
  }
`;

/** The report for [from, to), filtered as the variables say. */
export async function fetchIssueMetrics(
  variables: IssueMetricsVariables
): Promise<IssueMetrics> {
  const data = await postGraphQL<{ issueMetrics: IssueMetrics }>(ISSUE_METRICS_QUERY, {
    ...variables,
  });
  return data.issueMetrics;
}

// ----------------------------------------------------------------------------
// Systems and partners, for the filters
// ----------------------------------------------------------------------------

export interface IssueSystemOption {
  id: string;
  name: string | null;
  partner: { id: string; oldId: string | null } | null;
  organization: { id: string; name: string } | null;
}

export interface IssuePartnerOption {
  /** The legacy integer id, what issueMetrics takes. */
  id: number;
  name: string;
}

const PAGE_SIZE = 100;
const MAX_PAGES = 100;

interface Connection<T> {
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  edges: Array<{ node: T }>;
}

export const ISSUE_SYSTEMS_QUERY = `
  query SupportIssueSystems($first: Int, $after: String) {
    listSystems(first: $first, after: $after, sort: [{ field: NAME, order: ASC }]) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          partner {
            id
            oldId
          }
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

/** Every system the token can read, by name, with its partner and organization. */
export async function fetchIssueSystems(): Promise<IssueSystemOption[]> {
  const systems: IssueSystemOption[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { listSystems: Connection<IssueSystemOption> | null } = await postGraphQL(
      ISSUE_SYSTEMS_QUERY,
      { first: PAGE_SIZE, after }
    );
    const connection = data.listSystems;
    (connection?.edges ?? []).forEach((edge) => systems.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return systems;
}

/**
 * The partners behind the systems, one entry each, named after the system's
 * organization (ant names partners through it) and sorted by name.
 */
export function partnerOptions(systems: IssueSystemOption[]): IssuePartnerOption[] {
  const byId = new Map<number, IssuePartnerOption>();
  systems.forEach((system) => {
    const id = Number(system.partner?.oldId);
    if (!system.partner?.oldId || !Number.isInteger(id)) return;
    const known = byId.get(id);
    const name = system.organization?.name || known?.name || `Partner ${id}`;
    byId.set(id, { id, name });
  });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** The systems of one partner (every system when no partner is picked). */
export function systemsOfPartner(
  systems: IssueSystemOption[],
  partnerId: number | null
): IssueSystemOption[] {
  if (partnerId === null) return systems;
  return systems.filter((s) => Number(s.partner?.oldId) === partnerId);
}

// ----------------------------------------------------------------------------
// The CSV
// ----------------------------------------------------------------------------

/** The CSV's URL for the same filters as the query. */
export function issuesCsvUrl(variables: IssueMetricsVariables): string {
  const params = new URLSearchParams();
  params.set('from', variables.from);
  params.set('to', variables.to);
  if (variables.partnerId !== null) params.set('partnerId', String(variables.partnerId));
  if (variables.systemId) params.set('systemId', variables.systemId);
  if (variables.hardwareFamily) params.set('hardwareFamily', variables.hardwareFamily);
  if (variables.sources) params.set('sources', variables.sources.join(','));
  params.set('countsAsNew', variables.countsAsNew ? 'true' : 'false');
  return `${runtimeConfig.API_BASE_URL}/issues/export.csv?${params.toString()}`;
}

/** The file name ant sent, or one from the range when it sent none. */
export function csvFilename(
  contentDisposition: string | null,
  variables: Pick<IssueMetricsVariables, 'from' | 'to'>
): string {
  const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match?.[1]) return decodeURIComponent(match[1]);
  return `issues-${variables.from.slice(0, 10)}-${variables.to.slice(0, 10)}.csv`;
}

/**
 * Downloads the CSV through ant's download pipeline (a Bearer token, like the
 * transactions export), saving it as the browser saves any file.
 */
export async function downloadIssuesCsv(variables: IssueMetricsVariables): Promise<void> {
  const { graphqlToken } = getCredentials();
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (graphqlToken) headers.Authorization = `Bearer ${graphqlToken}`;
  const response = await fetch(issuesCsvUrl(variables), { method: 'GET', headers });
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  }
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.error || body?.errors?.[0]?.message || body?.message || '';
    } catch (e) {
      detail = '';
    }
    throw new Error(detail || `The export failed with status ${response.status}`);
  }
  const blob = await response.blob();
  downloadBlob(blob, csvFilename(response.headers.get('content-disposition'), variables));
}

// ----------------------------------------------------------------------------
// The hook
// ----------------------------------------------------------------------------

export interface IssueMetricsState {
  metrics: IssueMetrics | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * The report for the variables, read again whenever they change or on
 * reload. A read that finishes after a newer one started is dropped, so the
 * numbers always match the filters on screen.
 */
export function useIssueMetrics(variables: IssueMetricsVariables): IssueMetricsState {
  const [metrics, setMetrics] = useState<IssueMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const latest = useRef(0);
  const key = JSON.stringify(variables);

  useEffect(() => {
    const request = latest.current + 1;
    latest.current = request;
    let cancelled = false;
    setLoading(true);
    fetchIssueMetrics(JSON.parse(key))
      .then((result) => {
        if (cancelled || latest.current !== request) return;
        setMetrics(result);
        setError(null);
      })
      .catch((e) => {
        if (cancelled || latest.current !== request) return;
        setError(errorText(e));
      })
      .finally(() => {
        if (cancelled || latest.current !== request) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, generation]);

  const reload = useCallback(() => setGeneration((g) => g + 1), []);

  return { metrics, loading, error, reload };
}
